import Header from '@/components/layouts/Header'
import { Tabs, TabsProps } from 'antd'
import { Head, usePage } from '@inertiajs/react'
import HomeEventCard from './components/HomeEventCard'

export interface Event {
  allow_attendee_app_access: boolean
  allow_public_registration: boolean
  client_name: string
  created_at: string
  description: string
  end_date: string
  end_time: string
  id: number
  location: string
  name: string
  slug: string
  start_date: string
  start_time: string
  status: 'draft' | 'live' | 'archived' | 'purged'
  updated_at: string
  logo?: string | null
  banner?: string | null
}

export type EventStatus = Event['status']

interface SharedProps {
  events?: Event[]
  current_user?: {
    id: number
    email: string
    name?: string
    global_admin: boolean
    primary_role?: 'owner' | 'crew'
  }
}

export default function EventsHome() {
  const { props } = usePage<SharedProps>()
  const { current_user, events } = props

  const renderEventCards = (filteredEvents: Event[] | undefined) => (
    <div className="mt-4 flex flex-wrap gap-4">
      {filteredEvents?.map((event) => (
        <HomeEventCard key={event.id} event={event} />
      ))}
    </div>
  )

  const tabItems: TabsProps['items'] = [
    {
      key: 'all',
      label: `All (${events?.length || 0})`,
      children: renderEventCards(events),
    },
    {
      key: 'live',
      label: `Live (${events?.filter((event) => event.status === 'live').length || 0})`,
      children: renderEventCards(events?.filter((event) => event.status === 'live')),
    },
    {
      key: 'draft',
      label: `Draft (${events?.filter((event) => event.status === 'draft').length || 0})`,
      children: renderEventCards(events?.filter((event) => event.status === 'draft')),
    },
    {
      key: 'archived',
      label: `Archived (${events?.filter((event) => event.status === 'archived').length || 0})`,
      children: renderEventCards(events?.filter((event) => event.status === 'archived')),
    },
    {
      key: 'purged',
      label: `Purged (${events?.filter((event) => event.status === 'purged').length || 0})`,
      children: renderEventCards(events?.filter((event) => event.status === 'purged')),
    },
  ]

  // Determine user role to display
  const getUserRole = () => {
    if (!current_user) return undefined
    if (current_user.global_admin) return 'Admin'
    if (current_user.primary_role === 'owner') return 'Event Owner'
    if (current_user.primary_role === 'crew') return 'Event Crew'
    return 'Event Owner' // Fallback
  }

  return (
    <div className="bg-gray-100 h-[100vh]">
      <Head title="Home" />
      <Header
        loggedInUser={
          current_user
            ? {
                name: current_user.name || current_user.email,
                role: getUserRole(),
                email: current_user.email,
              }
            : undefined
        }
      />
      <div className="container mx-auto px-4 pt-8">
        <h2 className="text-xl mb-4">
          Hello{' '}
          <span className="font-semibold">
            {current_user ? `${current_user.name || current_user.email}` : ''}
          </span>
          , welcome back!
        </h2>
        <Tabs items={tabItems} size="small" />
      </div>
    </div>
  )
}
